import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import { useAuth } from './useAuth';

const TOASTED_KEY = 'notif_toasted_v1';

function loadToasted() {
  try {
    const raw = localStorage.getItem(TOASTED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.slice(-500) : []);
  } catch {
    return new Set();
  }
}

function persistToasted(set) {
  try {
    localStorage.setItem(TOASTED_KEY, JSON.stringify([...set].slice(-500)));
  } catch {
    /* storage unavailable */
  }
}

/**
 * Subscribe to Convex vendorNotifications for the logged-in agent/admin.
 * Raises a one-off in-app toast the first time each message is seen. Rows stay
 * so the bell can show history; dedupe is localStorage by messageId.
 * Browser/OS push for a closed tab is sent separately (same messageId, no second Convex row).
 *
 * Initial subscription snapshot is seeded silently (no toast backlog on login).
 */
export function useAgentPaymentAlerts() {
  const { user, setNotification } = useAuth();
  const userId = Number(user?.id || user?.user_id || 0) || null;
  const toastedRef = useRef(loadToasted());
  const seededForUserRef = useRef(null);

  const rows = useQuery(
    api.services.listVendorNotifications,
    userId ? { recipientUserId: userId, limit: 50 } : 'skip',
  );

  const notifications = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  useEffect(() => {
    seededForUserRef.current = null;
  }, [userId]);

  useEffect(() => {
    if (!userId || rows === undefined) return;

    // First snapshot after login/connect: mark existing rows seen without toasting.
    if (seededForUserRef.current !== userId) {
      let added = false;
      for (const n of notifications) {
        const messageId = n?.messageId;
        if (!messageId || toastedRef.current.has(messageId)) continue;
        toastedRef.current.add(messageId);
        added = true;
      }
      if (added) persistToasted(toastedRef.current);
      seededForUserRef.current = userId;
      return;
    }

    if (!notifications.length) return;
    let added = false;
    for (const n of notifications) {
      const messageId = n?.messageId;
      if (!messageId || toastedRef.current.has(messageId)) continue;
      toastedRef.current.add(messageId);
      added = true;

      const data = n?.data || {};
      const type = String(data.type || data.notification_type || '').toUpperCase();
      const title = n.title || 'Notification';
      const body = n.body || '';

      const isPaymentFailed =
        type === 'CUSTOMER_PAYMENT_FAILED' || /payment failed/i.test(title);
      const isSla =
        type === 'DISPATCH_SLA_BREACH' ||
        type === 'DISPATCH_ESCALATION' ||
        type === 'VENDOR_SUSPENDED' ||
        type === 'VENDOR_WITHDREW' ||
        type === 'DISPUTE_RAISED' ||
        type === 'COMPLETION_OTP_ESCALATION' ||
        type === 'CUSTOMER_CANCELLATION_REJECTED' ||
        type === 'CUSTOMER_CANCELLATION_AUTO_SETTLED';
      if (typeof setNotification === 'function') {
        setNotification({
          type: isPaymentFailed ? 'customer_payment_failed' : isSla ? 'sla_breach' : 'push',
          message: body || title,
          title,
          serviceId: data.service_id,
          caseId: data.case_id,
        });
      }
    }
    if (added) persistToasted(toastedRef.current);
  }, [userId, rows, notifications, setNotification]);
}
